class GeoRedirectOverlay {
    constructor(element) {
        this.element = element;
        this.isEnabled = element.dataset.enable === 'true';
        this.checkFrequency = element.dataset.checkFrequency || 'once';
        this.currentStore = element.dataset.currentStore;
        this.currentCountries = this.parseCountries(element.dataset.currentCountries);
        this.showClose = element.dataset.showClose === 'true';
        this.cookieDays = parseInt(element.dataset.cookieDays) || 30;

        this.closeButton = element.querySelector('.geo-redirect-overlay__close');
        this.stayButton = element.querySelector('.geo-redirect-overlay__stay-button');
        this.backdrop = element.querySelector('.geo-redirect-overlay__backdrop');
        this.storeItems = element.querySelectorAll('.geo-redirect-overlay__store-item');

        this.COOKIE_NAME = 'geo_redirect_choice';
        this.SESSION_KEY = 'geo_redirect_shown';
        this.BANNER_DISMISSED_KEY = 'geo_banner_dismissed';

        // Wait for page to be fully loaded before showing banner
        // This ensures banner doesn't appear before page content
        if (document.readyState === 'complete') {
            this.initBanner();
        } else {
            window.addEventListener('load', () => {
                this.initBanner();
            });
        }

        if (this.isEnabled) {
            this.init();
        }
    }

    initBanner() {
        // Additional delay to ensure smooth appearance after page load
        setTimeout(() => {
            this.banner = document.querySelector('[data-geo-banner]');
            this.bannerButton = document.querySelector('[data-open-geo-overlay]');
            this.bannerDismiss = document.querySelector('[data-dismiss-banner]');

            console.log('Banner elements:', {
                banner: this.banner,
                button: this.bannerButton,
                dismiss: this.bannerDismiss
            });

            // Bind banner events
            this.bindBannerEvents();

            // Check banner visibility based on IP
            this.detectUserLocationForBanner();
        }, 100); // 100ms delay after page load
    }

    init() {
        // Check if we should show the overlay
        if (this.shouldShowOverlay()) {
            this.detectUserLocation();
        }

        // Bind events
        this.bindEvents();
    }

    bindEvents() {
        // Close button
        if (this.closeButton) {
            this.closeButton.addEventListener('click', () => this.closeOverlay());
        }

        // Stay button
        if (this.stayButton) {
            this.stayButton.addEventListener('click', () => this.handleStayClick());
        }

        // Backdrop click
        if (this.showClose && this.backdrop) {
            this.backdrop.addEventListener('click', () => this.closeOverlay());
        }

        // Store items click
        this.storeItems.forEach(item => {
            item.addEventListener('click', (e) => this.handleStoreClick(e, item));
        });

        // Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOverlayVisible()) {
                this.closeOverlay();
            }
        });
    }

    bindBannerEvents() {
        console.log('Binding banner events...');

        // Banner button click - open overlay manually
        if (this.bannerButton) {
            console.log('Banner button found, adding click listener');
            this.bannerButton.addEventListener('click', (e) => {
                console.log('Banner button clicked!');
                e.preventDefault();
                this.openOverlayManually();
            });
        } else {
            console.warn('Banner button not found!');
        }

        // Banner dismiss button - only hide temporarily for current session
        if (this.bannerDismiss) {
            console.log('Banner dismiss button found, adding click listener');
            this.bannerDismiss.addEventListener('click', () => {
                console.log('Banner dismiss clicked');
                this.dismissBanner();
            });
        }
    }

    openOverlayManually() {
        console.log('Opening overlay manually');

        // Save current scroll position
        this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

        // Add class to body to prevent scrolling
        document.body.classList.add('geo-redirect-overlay-open');
        document.body.style.top = `-${this.scrollPosition}px`;
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';

        // Show overlay
        this.element.classList.add('active');

        // Trigger animation
        setTimeout(() => {
            this.element.classList.add('visible');
        }, 10);

        console.log('Overlay classes:', this.element.classList.toString());
        console.log('Body classes:', document.body.classList.toString());
    }

    dismissBanner() {
        if (this.banner) {
            // Only hide for current page session, will show again on refresh if IP is wrong
            this.banner.classList.add('hidden');
        }
    }

    shouldShowOverlay() {
        // Check if user has already made a choice
        const userChoice = this.getCookie(this.COOKIE_NAME);
        if (userChoice) {
            return false;
        }

        // Check frequency
        if (this.checkFrequency === 'once') {
            const shownInSession = sessionStorage.getItem(this.SESSION_KEY);
            if (shownInSession) {
                return false;
            }
        } else if (this.checkFrequency === 'daily') {
            const lastCheck = localStorage.getItem('geo_redirect_last_check');
            const today = new Date().toDateString();
            if (lastCheck === today) {
                return false;
            }
            localStorage.setItem('geo_redirect_last_check', today);
        }

        return true;
    }

    async detectUserLocation() {
        try {
            // Try to get location from multiple sources
            const country = await this.getUserCountry();

            if (country && !this.isCountryInCurrentStore(country)) {
                // User is from a different region
                this.showOverlay(country);
            } else {
                // User is in the correct region
                this.markAsShown();
            }
        } catch (error) {
            console.error('GeoRedirectOverlay: Failed to detect location', error);
            this.markAsShown();
        }
    }

    async detectUserLocationForBanner() {
        try {
            // Try to get location from multiple sources
            const country = await this.getUserCountry();

            if (country && !this.isCountryInCurrentStore(country)) {
                // User is from a different region, show banner
                if (this.banner) {
                    this.banner.classList.remove('hidden');
                    // Add loaded class to trigger slide-in animation
                    setTimeout(() => {
                        this.banner.classList.add('loaded');
                        // Scroll to top when banner appears
                        window.scrollTo({
                            top: 0,
                            behavior: 'smooth'
                        });
                    }, 50);
                    console.log(`User from ${country}, showing banner`);
                }
            } else {
                // User is in the correct region, hide banner
                if (this.banner) {
                    this.banner.classList.add('hidden');
                    console.log(`User from ${country || 'unknown'}, hiding banner`);
                }
            }
        } catch (error) {
            console.error('GeoRedirectOverlay: Failed to detect location for banner', error);
            // On error, hide banner to avoid confusion
            if (this.banner) {
                this.banner.classList.add('hidden');
            }
        }
    }

    async getUserCountry() {
        // Method 1: Try Shopify's geolocation (if available)
        if (window.Shopify && window.Shopify.country) {
            return window.Shopify.country;
        }

        // Method 2: Try CloudFlare (if available)
        if (typeof CF !== 'undefined' && CF.country) {
            return CF.country;
        }

        // Method 3: Try ipapi.co (free service, 1000 requests per day)
        try {
            const response = await fetch('https://ipapi.co/json/');
            if (response.ok) {
                const data = await response.json();
                return data.country_code;
            }
        } catch (e) {
            console.log('ipapi.co failed, trying fallback');
        }

        // Method 4: Try ip-api.com (free service)
        try {
            const response = await fetch('http://ip-api.com/json/?fields=countryCode');
            if (response.ok) {
                const data = await response.json();
                return data.countryCode;
            }
        } catch (e) {
            console.log('ip-api.com failed');
        }

        // Method 5: Try cloudflare trace
        try {
            const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace');
            if (response.ok) {
                const data = await response.text();
                const locMatch = data.match(/loc=([A-Z]{2})/);
                if (locMatch) {
                    return locMatch[1];
                }
            }
        } catch (e) {
            console.log('cloudflare trace failed');
        }

        return null;
    }

    isCountryInCurrentStore(country) {
        return this.currentCountries.includes(country.toUpperCase());
    }

    parseCountries(countriesString) {
        if (!countriesString) return [];
        return countriesString.split(',').map(c => c.trim().toUpperCase());
    }

    showOverlay(userCountry) {
        this.userCountry = userCountry;

        // Save current scroll position
        this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

        // Add class to body to prevent scrolling
        document.body.classList.add('geo-redirect-overlay-open');
        document.body.style.top = `-${this.scrollPosition}px`;
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';

        // Show overlay
        this.element.classList.add('active');

        // Trigger animation
        setTimeout(() => {
            this.element.classList.add('visible');
        }, 10);

        // Mark as shown
        this.markAsShown();

        // Highlight the recommended store
        this.highlightRecommendedStore(userCountry);
    }

    closeOverlay() {
        if (!this.showClose) return;

        this.element.classList.remove('visible');

        setTimeout(() => {
            this.element.classList.remove('active');
            document.body.classList.remove('geo-redirect-overlay-open');

            // Restore scroll position
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            if (this.scrollPosition !== undefined) {
                window.scrollTo(0, this.scrollPosition);
            }
        }, 300);

        // Save that user chose to stay
        this.setCookie(this.COOKIE_NAME, this.currentStore, this.cookieDays);
    }

    handleStayClick() {
        this.setCookie(this.COOKIE_NAME, this.currentStore, this.cookieDays);
        this.closeOverlay();
    }

    handleStoreClick(e, item) {
        const storeCode = item.dataset.storeCode;
        const storeUrl = item.getAttribute('href');

        if (!storeUrl) {
            e.preventDefault();
            return;
        }

        // Save user's choice
        this.setCookie(this.COOKIE_NAME, storeCode, this.cookieDays);

        // Let the link navigation happen naturally
    }

    highlightRecommendedStore(userCountry) {
        this.storeItems.forEach(item => {
            const countries = this.parseCountries(item.dataset.storeCountries);
            if (countries.includes(userCountry.toUpperCase())) {
                item.style.borderColor = '#000';
                item.style.background = '#f0f8ff';

                // Add a "Recommended" badge
                const badge = document.createElement('span');
                badge.className = 'geo-redirect-overlay__recommended-badge';
                badge.textContent = 'Recommended';
                badge.style.cssText = `
          position: absolute;
          top: -8px;
          right: 16px;
          background: #000;
          color: #fff;
          font-size: 11px;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 600;
        `;
                item.style.position = 'relative';
                item.appendChild(badge);
            }
        });
    }

    markAsShown() {
        if (this.checkFrequency === 'once') {
            sessionStorage.setItem(this.SESSION_KEY, 'true');
        }
    }

    isOverlayVisible() {
        return this.element.classList.contains('active');
    }

    // Cookie helper methods
    setCookie(name, value, days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        const expires = `expires=${date.toUTCString()}`;
        document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
    }

    getCookie(name) {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGeoRedirectOverlay);
} else {
    initGeoRedirectOverlay();
}

function initGeoRedirectOverlay() {
    const overlayElement = document.querySelector('[data-geo-redirect]');
    if (overlayElement) {
        new GeoRedirectOverlay(overlayElement);
    }
}

// Re-initialize if section is re-rendered (Shopify theme editor)
document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('[data-geo-redirect]')) {
        initGeoRedirectOverlay();
    }
});
